import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Divider,
  Paper,
  ListItem,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ApiGetQuizQuestions,
  ApiPostQuizQuestion,
  ApiPutquizBrief,
  ApiPutQuizQuestion,
} from "../../../apiService/Core/Apicall.tsx";
import PatchedReactQuill from "../ManageVenue/PatchedReactQuill.tsx";

interface Option {
  option_id: number;
  option_label: string;
}

interface Question {
  id: number;
  text: string;
  options: Option[];
  correctAnswer: string;
  description: string;
}

export default function QuizManagement() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [description, setDescription] = useState("");
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(
    null
  );
  const [quizBrief, setQuizBrief] = useState("");

  const location = useLocation();
  const clientEntityData = location.state?.store;

  const handleAddOption = () => {
    const newId =
      options.length > 0 ? options[options.length - 1].option_id + 1 : 1;
    setOptions([...options, { option_id: newId, option_label: "" }]);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index].option_label = value;
    setOptions(newOptions);
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestionId(question.id);
    setQuestionText(question.text);
    setOptions(
      question.options.map((opt, i) =>
        typeof opt === "string"
          ? { option_id: i + 1, option_label: opt }
          : { ...opt }
      )
    );
    setCorrectAnswer(question.correctAnswer);
    setDescription(question.description);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingQuestionId(null);
    resetForm();
  };

  const fetchQuestions = async () => {
    const data = `?clientEntityId=${clientEntityData.clientEntityId}`;
    try {
      const response = await ApiGetQuizQuestions(data);
      const questionsData = response?.data || [];

      const parsedQuestions: Question[] = questionsData.map((item: any) => ({
        id: item.quiz_question_id,
        text: item.question_text,
        options: JSON.parse(item.options),
        correctAnswer: item.answer.toString(),
        description: item.questionDescription,
      }));

      setQuestions(parsedQuestions);
      setQuizBrief(clientEntityData.quizBrief);
    } catch (error) {
      console.error("Failed to fetch quiz questions:", error);
      toast.error("Failed to load quiz questions");
    }
  };

  useEffect(() => {
    if (clientEntityData?.clientEntityId) {
      fetchQuestions();
    }
  }, [clientEntityData]);

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      alert("Question text is required");
      return;
    }

    if (options.some((option) => !option.option_label.trim())) {
      alert("All options must have content");
      return;
    }

    if (!correctAnswer) {
      alert("Please select the correct answer");
      return;
    }

    const questionData: any = {
      question_text: questionText,
      options: options,
      answer: parseInt(correctAnswer, 10),
      questionDescription: description,
    };

    try {
      if (editingQuestionId) {
        questionData.quiz_question_id = editingQuestionId;
        await ApiPutQuizQuestion(questionData);
        toast.success("Question updated successfully");
      } else {
        questionData.clientEntityId = clientEntityData.clientEntityId;
        await ApiPostQuizQuestion(questionData);
        toast.success("Question added successfully");
      }

      await fetchQuestions();
      handleCloseDialog();
      setQuizBrief(quizBrief);
    } catch (error) {
      console.error("Error submitting question:", error);
      toast.error("Failed to submit question. Please try again.");
    }
  };

  const resetForm = () => {
    setQuestionText("");
    setOptions([]);
    setCorrectAnswer("");
    setDescription("");
  };

  const filteredQuestions = questions.filter((question) =>
    question.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddQuestionBrief = async () => {
    const data = {
      clientEntityId: clientEntityData?.clientEntityId,
      quizBrief: quizBrief
    }
    try {
      const response = await ApiPutquizBrief(data)
      toast.success("Quiz brief updated successfully!")
    } catch (error) {
      toast.error(error);
    }
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: 3 }}>
      <Box sx={{ paddingBottom: 3 }}>
        <label>Quiz brief</label>
        <PatchedReactQuill
          setEditorValue={setQuizBrief}
          editorValue={quizBrief}
          isUpdate={false}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
           handleAddQuestionBrief()
          }}
          className="table-row-background"
        >
          Submit
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Quiz</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm(); // ensure clean state
            setOptions([{ option_id: 1, option_label: "" }]); // add one default option
            setIsDialogOpen(true);
          }}
          className="table-row-background"
        >
          Add Question
        </Button>
      </Box>

      <TextField
        fullWidth
        placeholder="Search your Question here..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {filteredQuestions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No questions added yet. Click "Add Question" to create your first
            question.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {filteredQuestions.map((question) => (
            <Paper key={question.id} sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography color="#747247">
                  Question ID: #{question.id}
                </Typography>
                <Typography>|</Typography>
                <Typography sx={{ fontWeight: "bold" }}>Type: Radio</Typography>
                <Typography>|</Typography>
                <Box
                  sx={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleEditQuestion(question)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                Q: {question.text}
              </Typography>

              <Grid container spacing={2}>
                {question.options.map((option, index) => (
                  <Grid item xs={12} md={6} key={option.option_id}>
                    <ListItem disablePadding>
                      <Typography>
                        {index + 1}. {option.option_label}
                      </Typography>
                    </ListItem>
                  </Grid>
                ))}
              </Grid>

              {question.description && (
                <Typography sx={{ mt: 2, fontSize: "0.9rem" }}>
                  Description: {question.description}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* Dialog for Add/Edit */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingQuestionId ? "Edit Question" : "Add Question"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 1 }}>
            <TextField
              label="Question Text *"
              multiline
              rows={3}
              fullWidth
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Enter your question here..."
            />

            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Options</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                  variant="outlined"
                >
                  Add Option
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {options.map((option, index) => (
                  <Box key={option.option_id} sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      fullWidth
                      value={option.option_label}
                      onChange={(e) =>
                        handleOptionChange(index, e.target.value)
                      }
                      placeholder={`Option ${index + 1}`}
                    />
                    {options.length > 1 && (
                      <IconButton onClick={() => handleRemoveOption(index)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Select Correct Answer
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                {options.map((option, index) => (
                  <FormControlLabel
                    key={option.option_id}
                    control={
                      <Checkbox
                        checked={correctAnswer === option.option_id.toString()}
                        onChange={() =>
                          setCorrectAnswer(option.option_id.toString())
                        }
                      />
                    }
                    label={`${index + 1}. ${option.option_label}`}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              label="Question Description"
              multiline
              rows={2}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter question description (optional)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            className="table-row-background"
            variant="contained"
          >
            {editingQuestionId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
